import { Change, Day, TimeSlot } from '@aifs-shared/calendar/day';
import { BaseResponse } from '@aifs-shared/common/base-response';
import { BaseService } from '@aifs-shared/common/base-service';
import { ResponseData } from '@aifs-shared/common/response-data';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { Observable, of, Subject, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { CommentDetailResponse } from '@aifs-shared/comments/comments';
import { DateTime } from 'luxon';
import { InterviewerDetailChanged } from './interviewer-detail-changed';

@Injectable({
    providedIn: 'root'
})
export class InterviewerService extends BaseService {

    constructor(private http: HttpClient) { super(); }

    public getInterviewStatusDescription(status: string): string {
        let desc = 'Unknown';
        switch (status) {
            case 'REQ': desc = 'Requested'; break;
            case 'PEND': desc = 'Pending'; break;
            case 'PASS': desc = 'Recommended'; break;
            case 'FAIL': desc = 'Non-recommended'; break;
            case 'EXPR': desc = 'Expired'; break;
            case 'REJD': desc = 'Rejected'; break;
        }
        return desc;
    }

    public isInterviewComplete(status: string): boolean {
        return (
            status === 'PASS' ||
            status === 'FAIL');
    }


    public getOnlineInterviewersForApplication(applicationId: number): Observable<DropdownInterviewersResponse> {
        const s = new Subject<DropdownInterviewersResponse>();

        this.http
            .get<BaseResponse>(environment.ServiceUrl_InterviewersForApplicationOnline(applicationId))
            .subscribe({
                next: (data: any) => {
                    let response = this.getResultData<DropdownInterviewersResponse>(data);
                    s.next(response);
                },
                error: (error: any) => {
                    this.handleError(error);
                }
            });

        return s;
    }

    getInterviewersForAgent(agencyId: number): Observable<InterviewerSearchResponse> {
        const subject = new Subject<InterviewerSearchResponse>();

        this
            .http
            .get<BaseResponse>(environment.ServiceUrl_Agency_Interviewers(agencyId))
            .pipe(
                catchError(this.handleError)
            )
            .subscribe({
                next: (data) => {
                    const response = this.getResultData<InterviewerSearchResponse>(data);
                    subject.next(response);
                },
                error: (error) => {
                    subject.error(error);
                }
            });

        return subject;
    }

    getInterviewersForStaff(staffId: number): Observable<InterviewerSearchResponse> {
        const subject = new Subject<InterviewerSearchResponse>();
        // console.log(`Calling: ${environment.ServiceUrl_Agency_StaffInterviewers(staffId)}`);
        this
            .http
            .get<BaseResponse>(environment.ServiceUrl_Agency_StaffInterviewers(staffId))
            .pipe(
                catchError(this.handleError)
            )
            .subscribe({
                next: (data) => {
                    const response = this.getResultData<InterviewerSearchResponse>(data);
                    subject.next(response);
                },
                error: (error) => {
                    subject.error(error);
                }
            });

        return subject;
    }

    getInterviewerDetails(interviewerId: number): Observable<AgencyInterviewerDetailsResponse> {
        const subject = new Subject<AgencyInterviewerDetailsResponse>();

        this
            .http
            .get<BaseResponse>(environment.ServiceUrl_Agent_Interviewer_Details(interviewerId))
            .pipe(
                catchError(this.handleError)
            )
            .subscribe({
                next: (data) => {
                    const response = this.getResultData<AgencyInterviewerDetailsResponse>(data);
                    subject.next(response);
                },
                error: (error) => {
                    subject.error(error);
                }
            });

        return subject;
    }

    getInterviewReportForApplication(applicationId: number): Observable<InterviewReport | null> {
        const subject = new Subject<InterviewReport | null>();

        this
            .http
            .get<BaseResponse>(environment.ServiceUrl_InterviewReport(applicationId))
            .pipe(
                catchError((err: HttpErrorResponse) => {
                    if (err.status === 404) return of(null);
                    return throwError(() => err);
                })
            )
            .subscribe({
                next: (data) => {
                    if (data) {
                        const response = this.getResultData<InterviewReport>(data);
                        subject.next(response);
                    } else {
                        subject.next(null);
                    }
                },
                error: (error) => {
                    subject.error(error);
                }
            });

        return subject;
    }

    getInterviewSlotsForInterviewer(interviewerId: number): Observable<InterviewSlotResponse> {
        const subject = new Subject<InterviewSlotResponse>();

        this
            .http
            .get<BaseResponse>(environment.ServiceUrl_OnlineInterviewSlots(interviewerId))
            .pipe(
                catchError(this.handleError)
            )
            .subscribe({
                next: (data) => {
                    const response = this.getResultData<InterviewSlotResponse>(data);
                    // console.log(`Interview Slots Response:`, response);
                    subject.next(response);
                },
                error: (error) => {
                    subject.error(error);
                }
            });

        return subject;
    }

    updateBioAndNotes(interviewerId: number, notes: string, bio: string, messageTemplate: string): Observable<boolean> {
        const s = new Subject<boolean>();

        this
            .http
            .post<BaseResponse>(environment.ServiceUrl_InterviewerProfile_Update(interviewerId),
                { interviewerId: interviewerId, notes: notes, bio: bio, messageTemplate: messageTemplate })
            .pipe(
                catchError(this.handleError)
            )
            .subscribe({
                next: (data) => {
                    const response = this.getResultData<boolean>(data);
                    s.next(response);
                },
                error: (error) => {
                    s.error(error);
                }
            });

        return s;
    }

    updateInterviewSlotAvailability(interviewerId: number, slots: UpdatedSlot[], excludeDateFrom: string): Observable<InterviewSlot> {
        const subject = new Subject<InterviewSlot>();

        this
            .http
            .post<BaseResponse>(
                environment.ServiceUrl_OnlineInterviewSlotUpdateAvailability(interviewerId),
                {
                    interviewerId: interviewerId,
                    slots: slots,
                    exclusionDateFrom: excludeDateFrom
                })
            .pipe(catchError(this.handleError))
            .subscribe({
                next: (data) => {
                    const response = this.getResultData<UpdateInterviewSlotResponse>(data);
                    if (response.success) {
                        subject.next(response.success);
                    } else {
                        throwError(() => new Error(`Couldn't update the availability of slots!`));
                    }
                },
                error: (error) => {
                    subject.error(error);
                }
            });

        return subject;
    }

    updateInterviewSlotExclusions(interviewerId: number, changes: Change[]): Observable<boolean> {
        const s = new Subject<boolean>();

        const request: SlotExclusionChangeRequest[] = [];
        changes.forEach(s => {
            if (s.newValue !== s.originalValue) {
                request.push(new SlotExclusionChangeRequest(s.date.toISODate()!, s.timeSlot, s.newValue));
            }
        })

        this
            .http
            .post<BaseResponse>(
                environment.ServiceUrl_OnlineInterviewSlotUpdateExclusions(interviewerId),
                {
                    interviewerId: interviewerId,
                    changes: request
                })
            .pipe(catchError(this.handleError))
            .subscribe({
                next: (data) => {
                    const response = this.getResultData<UpdateInterviewSlotResponse>(data);
                    if (response.success) {
                        s.next(true); //response.success);
                    } else {
                        throwError(() => new Error(`Couldn't update the availability of slots!`));
                    }
                },
                error: (error) => {
                    s.error(error);
                }
            });
        return s;
    }

    getInterviewsForInterviewer(interviewerId: number, startDate: string, endDate: string): Observable<InterviewCalendarItem[]> {
        const subject = new Subject<InterviewCalendarItem[]>();

        this
            .http
            .post<BaseResponse>(environment.ServiceUrl_OnlineInterviewsAvailability(interviewerId),
                {
                    startDate: startDate,
                    endDate: endDate
                })
            .pipe(
                catchError(this.handleError)
            )
            .subscribe(
                {
                    next: (data) => {
                        const response = this.getResultData<BookedInterviewsResponse>(data);
                        if (response) {
                            subject.next(response.items);
                        } else {
                            subject.complete();
                        }
                    },
                    error: (error) => {
                        subject.error(error);
                    }
                });

        return subject;
    }

    getUpcomingInterviewsForInterviewer(interviewerId: number): Observable<UpcomingInterviews> {
        const subject = new Subject<UpcomingInterviews>();

        this
            .http
            .get<BaseResponse>(environment.ServiceUrl_OnlineInterviewsUpcoming(interviewerId))
            .pipe(
                catchError(this.handleError)
            )
            .subscribe({
                next: (data) => {
                    const response = this.getResultData<UpcomingInterviews>(data);
                    subject.next(response);
                },
                error: (error) => {
                    subject.error(error);
                }
            });

        return subject;
    }

    getUpcomingInterviewsForAgent(agentId: number): Observable<UpcomingInterviews> {
        const subject = new Subject<UpcomingInterviews>();

        this
            .http
            .get<BaseResponse>(environment.ServiceUrl_OnlineInterviewsUpcoming_Agent(agentId))
            .pipe(
                catchError(this.handleError)
            )
            .subscribe({
                next: (data) => {
                    const response = this.getResultData<UpcomingInterviews>(data);
                    subject.next(response);
                },
                error: (error) => {
                    subject.error(error);
                }
            });

        return subject;
    }

    getUpcomingInterviewsForStaff(staffId: number): Observable<UpcomingInterviews> {
        const subject = new Subject<UpcomingInterviews>();

        this
            .http
            .get<BaseResponse>(environment.ServiceUrl_OnlineInterviewsUpcoming_Staff(staffId))
            .pipe(
                catchError(this.handleError)
            )
            .subscribe({
                next: (data) => {
                    const response = this.getResultData<UpcomingInterviews>(data);
                    subject.next(response);
                },
                error: (error) => {
                    subject.error(error);
                }
            });

        return subject;
    }

    getApplicantByQueueForInterviewer(interviewerId: number, queueType: string): Observable<InterviewerQueueResponse> {
        const subject = new Subject<InterviewerQueueResponse>();

        const options = queueType ?
            { params: new HttpParams().set('q', queueType) } : {};

        this
            .http
            .get<BaseResponse>(environment.ServiceUrl_InterviewerQueueData(interviewerId), options)
            .pipe(
                catchError(this.handleError)
            )
            .subscribe({
                next: (data) => {
                    const response = this.getResultData<InterviewerQueueResponse>(data);
                    subject.next(response);
                },
                error: (error) => {
                    subject.error(error);
                }
            });

        return subject;
    }

    getApplicantByQueueForAgent(agentId: number, queueType: string): Observable<InterviewerQueueResponse> {
        const subject = new Subject<InterviewerQueueResponse>();

        const options = queueType ?
            { params: new HttpParams().set('q', queueType) } : {};

        this
            .http
            .get<BaseResponse>(environment.ServiceUrl_InterviewerQueueData_Agent(agentId), options)
            .pipe(
                catchError(this.handleError)
            )
            .subscribe({
                next: (data) => {
                    const response = this.getResultData<InterviewerQueueResponse>(data);
                    subject.next(response);
                },
                error: (error) => {
                    subject.error(error);
                }
            });

        return subject;
    }


    getApplicantByQueueForStaff(staffId: number, queueType: string): Observable<InterviewerQueueResponse> {
        const subject = new Subject<InterviewerQueueResponse>();

        const options = queueType ?
            { params: new HttpParams().set('q', queueType) } : {};

        this
            .http
            .get<BaseResponse>(environment.ServiceUrl_InterviewerQueueData_Staff(staffId), options)
            .pipe(
                catchError(this.handleError)
            )
            .subscribe({
                next: (data) => {
                    const response = this.getResultData<InterviewerQueueResponse>(data);
                    subject.next(response);
                },
                error: (error) => {
                    subject.error(error);
                }
            });

        return subject;
    }

    public updateInterviewerViewedApplication(interviewerId: number, applicationId: number, displayViewId: number = 0): Observable<BaseResponse> {
        return this.http
            .post<BaseResponse>(
                environment.ServiceUrl_InterviewerViewedApplication_InView(interviewerId, applicationId, displayViewId),
                {});
    }

    public generateTimeSlotsAndDays(slots: InterviewSlot[]): Day[] {
        let days = [];
        var day = 0;
        while (day < 8) {
            day++;
            const daySlots = slots.filter(s => s.dayOfWeek === day);
            if (daySlots && daySlots.length > 0) {
                const d = new Day(daySlots[0].day.substring(0, 3), true, day);
                daySlots.forEach(ds => {
                    d.slots.push(new TimeSlot(ds.availabilitySlotId, ds.startTime, ds.endTime, ds.isAvailable));
                });
                days.push(d);
            }
        }

        return days;
    }

    public confirmInterview(interviewId: number, interviewerId: number, applicationId: number): Observable<ConfirmInterviewResponse> {
        const s = new Subject<ConfirmInterviewResponse>();

        this
            .http
            .post<BaseResponse>(environment.ServiceUrl_InterviewConfirm(interviewId),
                {
                    applicationId: applicationId,
                    interviewId: interviewId,
                    interviewerId: interviewerId
                }
            )
            .subscribe({
                next: (response: ConfirmInterviewResponse) => {
                    // console.log(`next: ${response}`);
                    if (response && response !== 401) s.next(response); else s.error('A server error occured');
                },
                error: (error: any) => { s.error(error); }
            });

        return s;
    }

    public cancelInterview(interviewId: number, interviewerId: number, applicationId: number, rejectionReason: string): Observable<InterviewCancelled> {
        const s = new Subject<InterviewCancelled>();

        this
            .http
            .post<BaseResponse>(environment.ServiceUrl_InterviewCancel,
                {
                    applicationId: applicationId,
                    interviewId: interviewId,
                    interviewerId: interviewerId,
                    rejectReason: rejectionReason
                }
            )
            .subscribe({
                next: (response: BaseResponse) => {
                    const result = this.getResultData<CancelInterviewResponse>(response);
                    // console.log(`Got response: ${JSON.stringify(result)}`);
                    if (result && result.details) s.next(result.details); else s.error('A server error occured');
                },
                error: (error: any) => { s.error(error); }
            });

        return s;
    }

    public rejectApplicant(interviewId: number, interviewerId: number, applicationId: number, rejectReason: RejectReason, rejectNotes: string) {
        const subject = new Subject<boolean>();

        this
            .http
            .post<BaseResponse>(environment.ServiceUrl_InterviewReject,
                {
                    interviewId: interviewId,
                    interviewerId: interviewerId,
                    applicationId: applicationId,
                    rejectReason: rejectReason,
                    rejectNotes: rejectNotes
                })
            .pipe(
                catchError(this.handleError)
            )
            .subscribe({
                next: (data) => {
                    subject.next(true);
                },
                error: (error) => {
                    subject.error(error);
                }
            });

        return subject;
    }

    public onlineInterviewAvailability(countryCode: string): Observable<OnlineInterviewAvailability> {
        const s = new Subject<OnlineInterviewAvailability>();

        this.http
            .post<BaseResponse>(environment.ServiceUrl_Agent_OnlineInterviewAvailability,
                {
                    countryCode: countryCode
                })
            .subscribe(
                {
                    next: (data) => {
                        let response = this.getResultData<OnlineInterviewAvailability>(data);
                        s.next(response);
                    },
                    error: (error) => this.handleError(error)
                }
            );

        return s;
    }

    public getSlotDetailsForAgent(agentId: number, staffId: number, dayOfWeek: number, startTime: string, isoDate: string, countryCode: string): Observable<OnlineInterviewSlotAvailability> {
        const s = new Subject<OnlineInterviewSlotAvailability>();

        this.http
            .post<BaseResponse>(environment.ServiceUrl_Agent_OnlineInterviewAvailabilityBySlot,
                { agentId: agentId, staffId: staffId, day: dayOfWeek, startTime: startTime, isoDate: isoDate, countryCode: countryCode })
            .subscribe({
                next: (data) => {
                    let response = this.getResultData<OnlineInterviewSlotAvailability>(data);
                    s.next(response);
                },
                error: (error: any) => {
                    this.handleError(error);
                }
            });

        return s;
    }

    completeInterview(interviewId: number, interviewerName: string, applicantId: number, applicationId: number, interviewerId: number, programme: string, recommended: string, nonRecommendReason: string, certify: string, confirmedCurrentYearStudent: string, nonStudentSkills: string, lookingForwardToInUS: string): Observable<CompleteInterviewResponse> {
        const s = new Subject<CompleteInterviewResponse>();
        this.http
            .post<BaseResponse>(environment.ServiceUrl_InterviewComplete(interviewId),
                {
                    interviewId: interviewId,
                    applicantId: applicantId,
                    applicationId: applicationId,
                    interviewerId: interviewerId,
                    programme: programme,
                    recommended: recommended,
                    nonRecommendReason: nonRecommendReason,
                    certify: certify,
                    confirmedCurrentYearStudent: confirmedCurrentYearStudent,
                    nonStudentSkills: nonStudentSkills,
                    lookingForwardToInUS: lookingForwardToInUS,
                    interviewerName: interviewerName
                })
            .subscribe(
                {
                    next: (data) => {
                        let response = this.getResultData<CompleteInterviewResponse>(data);
                        s.next(response);
                    },
                    error: (error) => this.handleError(error)
                }
            );


        return s;
    }

    completeInterviewReport(interviewerId: number, applicationId: number, submit: boolean, studyingTime: string, englishNative: string, fluency: string, comprehension: string, speed: string, clarity: string, languageUse: string, studyingTimeSpan: string, otherName: string, whyCamp: string, relationshipWithFamily: string, yourFriends: string, particularCamp: string, behaviour: string, childcareExp: string, essentialRequirements: string, caTerms: string, firstImpression: string, personality: string, applicableSkills: string, additionalInfo: string): Observable<CompleteInterviewReportResponse> {
        const s = new Subject<CompleteInterviewReportResponse>();
        this.http
            .post<BaseResponse>(environment.ServiceUrl_WriteInterviewReport(applicationId),
                {
                    applicationId: applicationId,
                    interviewerId: interviewerId,
                    englishNative: englishNative,
                    fluency: fluency,
                    comprehension: comprehension,
                    speed: speed,
                    clarity: clarity,
                    languageUse: languageUse,
                    studyingTime: studyingTime,
                    studyingTimeSpan: studyingTimeSpan,
                    otherName: otherName,
                    whyCamp: whyCamp,
                    relationshipWithFamily: relationshipWithFamily,
                    yourFriends: yourFriends,
                    particularCamp: particularCamp,
                    behaviour: behaviour,
                    childcareExp: childcareExp,
                    essentialRequirements: essentialRequirements,
                    caTerms: caTerms,
                    firstImpression: firstImpression,
                    personality: personality,
                    applicableSkills: applicableSkills,
                    additionalInfo: additionalInfo,
                    isSubmitting: submit
                })
            .subscribe(
                {
                    next: (data) => {
                        let response = this.getResultData<CompleteInterviewResponse>(data);
                        s.next(response);
                    },
                    error: (error) => this.handleError(error)
                }
            );
        return s;
    }

    submitToOffice(applicationId: number, interviewerId: number): Observable<ApplicationSubmittedResponse> {
        const s = new Subject<ApplicationSubmittedResponse>();

        this.http
            .post<BaseResponse>(environment.ServiceUrl_Applicant_SubmitApplicationToOffice(applicationId),
                {
                    applicationId: applicationId,
                    interviewerId: interviewerId,
                })
            .subscribe(
                {
                    next: (data) => {
                        let response = this.getResultData<ApplicationSubmittedResponse>(data);
                        s.next(response);
                    },
                    error: (error) => this.handleError(error)
                });

        return s;
    }

    getInterviewerLocations(interviewerId: number) {
        const subject = new Subject<InterviewerLocationsResponse>();

        this
            .http
            .get<BaseResponse>(environment.ServiceUrl_Interviewer_Location(interviewerId))
            .pipe(
                catchError(this.handleError)
            )
            .subscribe({
                next: (data) => {
                    const response = this.getResultData<InterviewerLocationsResponse>(data);
                    subject.next(response);
                },
                error: (error) => {
                    subject.error(error);
                }
            });

        return subject;
    }

    insertInterviewerLocation(interviewerId: number,
        latitude: number,
        longitude: number,
        name: string,
        availability: string,
        isActive: boolean) {
        const subject = new Subject<boolean>();

        this
            .http
            .post<BaseResponse>(environment.ServiceUrl_Interviewer_Location(interviewerId),
                {
                    interviewerId: interviewerId,
                    longitude: longitude,
                    latitude: latitude,
                    name: name,
                    availability: availability,
                    isActive: isActive,
                })
            .pipe(
                catchError(this.handleError)
            )
            .subscribe({
                next: (data) => {
                    subject.next(true);
                },
                error: (error) => {
                    subject.error(error);
                }
            });

        return subject;
    }

    updateInterviewerLocation(interviewerId: number,
        interviewLocationId: number,
        latitude: number,
        longitude: number,
        name: string,
        availability: string,
        isActive: boolean) {
        const subject = new Subject<boolean>();

        this
            .http
            .put<BaseResponse>(environment.ServiceUrl_Interviewer_Location(interviewerId),
                {
                    interviewLocationId: interviewLocationId,
                    longitude: longitude,
                    latitude: latitude,
                    name: name,
                    availability: availability,
                    isActive: isActive,
                })
            .pipe(
                catchError(this.handleError)
            )
            .subscribe({
                next: (data) => {
                    subject.next(true);
                },
                error: (error) => {
                    subject.error(error);
                }
            });

        return subject;
    }

    deleteInterviewerLocation(interviewerId: number,
        interviewLocationId: number) {
        const subject = new Subject<DeleteInterviewerLocationResponse>();

        this
            .http
            .delete<BaseResponse>(environment.ServiceUrl_Interviewer_Location_InterviewLocationId(interviewerId, interviewLocationId))
            .subscribe({
                next: (data) => {
                    const response = this.getResultData<DeleteInterviewerLocationResponse>(data);
                    subject.next(response);
                },
                error: (error) => {
                    subject.error(error);
                }
            });

        return subject;
    }

    getInterviewDetails(applicationId: number) {
        const subject = new Subject<InterviewDetailsResponse>();

        this
            .http
            .get<BaseResponse>(environment.ServiceUrl_InterviewDetailsForApplication(applicationId))
            .pipe(
                catchError(this.handleError)
            )
            .subscribe({
                next: (data) => {
                    const response = this.getResultData<InterviewDetailsResponse>(data);
                    subject.next(response);
                },
                error: (error) => {
                    subject.error(error);
                }
            });

        return subject;
    }

    postInterviewPropose(
        applicationId: number,
        interviewId: number,
        locationId: number,
        interviewDate: string,
        interviewTime: string,
        notes: string
    ) {
        const subject = new Subject<boolean>();

        this
            .http
            .post<BaseResponse>(environment.ServiceUrl_ProposeInterview(applicationId),
                {
                    interviewId: interviewId,
                    locationId: locationId,
                    interviewDate: interviewDate,
                    interviewTime: interviewTime,
                    notes: notes
                })
            .pipe(
                catchError(this.handleError)
            )
            .subscribe({
                next: (data) => {
                    subject.next(true);
                },
                error: (error) => {
                    subject.error(error);
                }
            });

        return subject;

    }

    getComments(interviewerId: number): Observable<CommentDetailResponse> {
        let subject = new Subject<CommentDetailResponse>();

        this.http
            .get<BaseResponse>(environment.ServiceUrl_Agent_InterviewerComments(interviewerId))
            .subscribe({
                next: (data: BaseResponse) => {
                    const response = this.getResultData<CommentDetailResponse>(data);
                    subject.next(response);
                },
                error: (error: any) => {
                    subject.error(error);
                }
            });

        return subject;
    }

    public addComment(interviewerId: number, userId: number, comment: string): Observable<CommentDetailResponse> {
        let subject = new Subject<CommentDetailResponse>();

        this.http
            .post<BaseResponse>(environment.ServiceUrl_Agent_InterviewerComment_New(interviewerId),
                { userId: userId, interviewerId: interviewerId, text: comment })
            .subscribe({
                next: (data: BaseResponse) => {
                    const response = this.getResultData<CommentDetailResponse>(data);
                    subject.next(response);
                },
                error: (error: any) => {
                    subject.error(error);
                }
            });

        return subject;
    }

    setActiveState(userId: number, interviewerId: number, newActiveForWebState: boolean): Observable<boolean> {
        const s = new Subject<boolean>();
        this.http
            .post<BaseResponse>(environment.ServiceUrl_Agent_Interviewer_SetActiveForWeb(interviewerId),
                { userId: userId, interviewerId: interviewerId, activeForWeb: newActiveForWebState })
            .subscribe({
                next: (data: BaseResponse) => {
                    const response = this.getResultData<ToggleActiveResponse>(data);
                    s.next(response.success);
                },
                error: (error: any) => {
                    s.error(error);
                }
            });
        return s;
    }

    updateInterviewerLimits(userId: number, interviewerId: number, applicantLimit: number, overdueReportLimit: number): Observable<boolean> {
        const s = new Subject<boolean>();
        this.http
            .post<BaseResponse>(environment.ServiceUrl_Agent_Interviewer_UpdateLimits(interviewerId),
                {
                    userId: userId,
                    interviewerId: interviewerId,
                    applicantLimit: applicantLimit,
                    overdueReportLimit: overdueReportLimit
                })
            .subscribe({
                next: (data: BaseResponse) => {
                    const response = this.getResultData<UpdateLimitsResponse>(data);
                    s.next(response.success);
                },
                error: (error: any) => {
                    s.error(error);
                }
            });
        return s;
    }

    getOverdueReportCount(interviewerId: number): Observable<number> {
        const s = new Subject<number>();
        this.http
            .get<BaseResponse>(environment.ServiceUrl_Agent_Interviewer_OverdueCount(interviewerId))
            .subscribe({
                next: (data: BaseResponse) => {
                    const response = this.getResultData<GetOverdueReportsResponse>(data);
                    s.next(response.count);
                },
                error: (error: any) => {
                    s.error(error);
                }
            });
        return s;
    }

    getInterviewReportSubmitTime(interviewerId: number): Observable<InterviewReportSubmitTimeAverage> {
        const s = new Subject<InterviewReportSubmitTimeAverage>();
        this.http
            .get<BaseResponse>(environment.ServiceUrl_Agent_Interviewer_ReportSubmitTime(interviewerId))
            .subscribe({
                next: (data: BaseResponse) => {
                    const response = this.getResultData<GetInterviewReportAverageSubmitTimeResponse>(data);
                    s.next(response.details);
                },
                error: (error: any) => {
                    s.error(error);
                }
            });
        return s;
    }

    public getCountry(code: string): Observable<ApplicationCountry> {
        const s = new Subject<ApplicationCountry>();
        this.http
            .get<BaseResponse>(`${environment.ServiceUrl_Application_Lookup_CountryDetails(code)}`)
            .subscribe({
                next: (r: BaseResponse) => {
                    const c = this.getResultData<ApplicationCountryResponse>(r).country!;
                    s.next(c);
                },
                error: (error: any) => {
                    s.error(error);
                }
            });

        return s;
    }

    public assignInterviewerToApplication(interviewerId: number, applicationId: number): Observable<boolean> {
        const s = new Subject<boolean>();
        this.http
            .post<BaseResponse>(`${environment.ServiceUrl_InterviewerAssignToApplication(interviewerId, applicationId)}`,
                { interviewerId: interviewerId, applicationId: applicationId }
            ).subscribe({
                next: (r: BaseResponse) => {
                    console.log(`Assigned: ${JSON.stringify(r)}`);
                    s.next((r.resultCode && r.resultCode == 1 ? true : false));
                },
                error: (error: any) => {
                    s.error(error);
                }
            });

        return s;
    }

    notifyInterviewerDetailChanged(changeItem: InterviewerDetailChanged) {
        //console.log(`notify subscribers of a change: ${JSON.stringify(changeItem)}`);
        localStorage.setItem('interviewerChangeItem', JSON.stringify(changeItem));
        this.interviewerDetailsChanged$.next(changeItem);
    }

    public interviewerDetailsChanged$ = new Subject<InterviewerDetailChanged>();
}

export class AgencyInterviewerDetailsResponse implements ResponseData {
    details?: AgentInterviewerDetails;
}

export class InterviewerSearchResponse implements ResponseData {
    data: InterviewerRecord[] = [];
}

export class ApplicationViewData implements ResponseData {
    applicantsViewed: ApplicantViewRecord[] = [];
}

export class InterviewSlotResponse implements ResponseData {
    slots: InterviewSlot[] = [];
    exclusionFrom: string = '';
}

export class UpdateInterviewSlotResponse implements ResponseData {
    success?: InterviewSlot;
}

export class BookedInterviewsResponse implements ResponseData {
    items: InterviewCalendarItem[] = [];
}

export class InterviewReportResponse implements ResponseData {
    result?: InterviewReport;
}

export class UpcomingInterviews implements ResponseData {
    next?: InterviewDetails;
    thisWeek: InterviewDetails[] = [];
}

export class InterviewerQueueResponse implements ResponseData {
    details?: ApplicantQueue;
}

export class InterviewerLocationsResponse implements ResponseData {
    locations: InterviewerLocation[] = [];
}

export class InterviewDetailsResponse implements ResponseData {
    interviewDateTime?: Date;
    notes: string = '';
}

export class DeleteInterviewerLocationResponse implements ResponseData {
    wasDeleted: boolean = true;
}

export class DropdownInterviewersResponse {
    interviewers!: OnlineInterviewer[]
}
export class InterviewerLocation {
    interviewLocationId?: number;
    interviewerId?: number;
    longitude?: number;
    latitude?: number;
    name?: string;
    availability?: string;
    isActive?: boolean;
}

export class InterviewerQueueType {
    public static IncompleteInterviewReports = "IIR";
    public static CompletedInterviews = "CIV";
    public static ReadyToSubmit = "RTS";
    public static InterviewReady = "RDY";
    public static F2FFollowup = "F2F";
}

export class ConfirmInterviewResponse implements ResponseData {

}

export class CancelInterviewResponse implements ResponseData {
    details!: InterviewCancelled;
}

export class CompleteInterviewResponse implements ResponseData {
    success!: boolean;
}

export class CompleteInterviewReportResponse implements ResponseData {
    success!: boolean;
}

export class ApplicationSubmittedResponse implements ResponseData {
    applicant: any;
    success!: boolean;
}

export interface AgentInterviewerDetails {
    userid: number;
    id: number;
    code: number;
    interviewerAgencyID: number;
    firstName: string;
    lastName: string;
    telephoneNumber: string;
    countryCode: string;
    mobileNumber: string;
    email: string;
    azureLogin: string;
    timesToCall: string;
    notes: string;
    activeForWeb: boolean;
    active: boolean;
    description: string;
    profileURL: string;
    retainedPaymentsType: number;
    creditorID: number;
    isParent: boolean;
    applicantLimit: number;
    overdueReportLimit: number;
    interviewerBio: string;
    messageTemplate: string;
    agencyId: number;
    agencyCode: string;
    agencyName: string;
    agencyCountry: string;
    isOnlineInterviewer: boolean;
    regions: string;
    lastLoginDays: string;
    isPromoted: boolean;
}

export interface InterviewerRecord {
    userId: number;
    interviewerId: number;
    email: string;
    firstName: string;
    lastName: string;
    active: boolean;
    activeForWeb: boolean;
    applicantLimit: number;
    region?: number;
    regionName?: string;
    agencyName: string;

    country: string;
    lastLoginDays: string;
    profileUrl?: string;
    phone?: string;
    isPromoted: boolean;
}

export interface ApplicantViewRecord {
    applicantId: number;
    firstName: string;
    middleName?: string;
    lastName: string;
}

export interface InterviewReport {
    reportComplete: boolean;
    age: number;
    agencyId: number;
    applicationId: number;
    firstName: string;
    middleName: string;
    lastName: string;
    email: string;
    nationality: string;
    gender: string;
    dateofBirth: string;
    telephone: string;
    firstImpression: string;
    englishNative: string;
    programme: string;
    recommended: string;
    interviewerName: string;
    interviewDate: string;
    furtherNotes: string;
    earliestAvailable: string;
    dateDueHome: string;
    whyCamp: string;
    relationshipWithFamily: string;
    yourFriends: string;
    particularCamp: string;
    childCareExp: string;
    essentialRequirements: string;
    behaviour: string;
    caTerms: string;
    personality: string;
    applicableSkills: string;
    additionalInfo: string;
    otherName: string;
    confirmedCurrentYearStudent: string;
    nonStudentSkills: string;
    lookingForwardToInUS: string;
    confirmedMeetingNotes: string;

    fluency: string;
    comprehension: string;
    speed: string;
    clarity: string;
    use: string;
    languageUse: string;
    studyingTimeSpan: string;

}

export interface InterviewSlot {
    availabilitySlotId: number;
    interviewerId: number;
    dayOfWeek: number;
    startTime: string;
    endTime: string;
    startHour: number;
    startMinute: number;
    endHour: number;
    endMinute: number;
    isAvailable: boolean;
    day: string;
}

export class UpdatedSlot {
    constructor(availabilitySlotId: number, isAvailable: boolean) {
        this.availabilitySlotId = availabilitySlotId;
        this.isAvailable = isAvailable;
    }
    availabilitySlotId: number;
    isAvailable: boolean;
}

export interface InterviewCalendarItem {
    startDate: string;
    endDate: string;
    entryType: number; // 1 - interview, 2 - locked
    name: string;
}

export interface InterviewDetails {
    name: string;
    hour: string;
    date: string;
    applicantId: number;
    applicationId: number;
    interviewId: number;
    locationName: string;
    isOnline: boolean;
    isConfirmed: boolean;
    status: string;
    profile: string;
    displayDate: string;
    displayTime: string;
    interviewer: string;
    isLate: boolean;
    countryCode: string;
    interviewDateTime?: DateTime;
    onlineInterviewsAllowed: boolean;
}

export interface ApplicantQueue {
    title: string;
    items: ApplicantQueueItem[];
}

export interface ApplicantQueueItem {
    applicantId: number;
    applicationId: number;
    profile: string;
    name: string;
    status: number;
    statusInfo: string;
    userState: string;
    interviewerName: string;
    date: string;
    interviewDateTime?: DateTime;
    countryCode: string;
    isNewToUser?: boolean; // CA-3155
}

export class InterviewCancelled {
    success = false;
    reason = '';
}

export interface OnlineInterviewAvailability {
    totalInterviewers: number;
    calendar: OnlineInterviewCalendar;
}

export interface OnlineInterviewCalendar {
    days: OnlineInterviewDay[];
}

export interface OnlineInterviewSlotAvailability {
    slotName: string;
    available: string[];
    unavailable: string[];
}

export interface OnlineInterviewDay {
    dayOfWeek: number;
    isoDate: string;
    slots: OnlineInterviewSlot[];
}

export interface OnlineInterviewSlot {
    startTime: string;
    count: number;
}

export class SlotExclusionChangeRequest {
    date: string;
    slot: string;
    locked: boolean;
    constructor(date: string, slot: string, locked: boolean) {
        this.date = date;
        this.slot = slot;
        this.locked = locked;
    }
}

export class ToggleActiveResponse implements ResponseData { success!: boolean; }
export class UpdateLimitsResponse implements ResponseData { success!: boolean; }
export class GetOverdueReportsResponse implements ResponseData { count!: number; }
export class GetInterviewReportAverageSubmitTimeResponse implements ResponseData { details!: InterviewReportSubmitTimeAverage; }

export interface InterviewReportSubmitTimeAverage {
    interviewerId: number;
    submitDayAverage: number
}

export enum RejectReason {
    Basic = 1, English,
    MedicalCriminal,
    Motivation,
    Attitude,
    NoResponse,
    Cancel,
    Other
}

export class ApplicationCountryResponse implements ResponseData {
    country?: ApplicationCountry;
}

export class ApplicationCountry {
    code: string = "";
    name: string = "";
    isApplicationCountry: boolean = false;
    isRecruiting: boolean = false;
    recruitingForCurrentSeason: boolean = false;
    recruitingForNextSeason: boolean = false;
    onlineInterviewsAllowed: boolean = false;
    onlineInterviewsMigration: boolean = false;
    manualInterviewerAssignment = false;
}

export interface OnlineInterviewer {
    id: number;
    name: string;
    profileUrl?: string;
    notes?: string;
    bio?: string;
    applicantCount: number;
    applicantLimit: number;
    isPromoted: boolean;
    ratio: number;
    availability: number;
}